import React from 'react';
import PropTypes from 'prop-types';

import { Container } from '@material-ui/core';

import ImageUploader from '@hydra/routes/pdf-editor/components/ImageUploader';
import ColorPreview from '@hydra/routes/pdf-editor/components/ColorPreview';
import ColorPickers from '@hydra/routes/pdf-editor/components/ColorPickers';
import EditorFormRow from '@hydra/routes/pdf-editor/components/EditorFormRow/EditorFormRow';
import Editor from '@hydra/routes/pdf-editor/components/EditorFormRow/Editor';
import EditorInline from '@hydra/routes/pdf-editor/components/EditorFormRow/EditorInline';
import ImagePreview from '@hydra/routes/pdf-editor/components/ImagePreview';
import RichEditor from '@hydra/routes/pdf-editor/components/RichEditor/RichEditor';
import RichEditorPreview from '@hydra/routes/pdf-editor/components/RichEditor/RichEditorPreview';
import FormToggle from '@hydra/routes/pdf-editor/components/FormToggle';
import InfoLabel from '@hydra/routes/pdf-editor/components/InfoLabel';
import HelpText from '@hydra/routes/pdf-editor/components/HelpText';
import Header from '@hydra/routes/pdf-editor/components/Header';
import { Icon, Translate } from '@hydra/common/components';

export const TabFooter = ({
  template,
  editing,
  editingValue,
  setEditingValue,
  setEditingField,
  resetEditing,
  saveFormField,
  savePdfFile,
  headerLocked,
  featuresLocked
}) => {
  return (
    <>
      <Header title='pdf_editor_tab_footer' hasLockedButton={headerLocked} />
      <Container>
        <EditorFormRow
          label={<Translate content='pdf_editor_add_footer' />}
          locked={featuresLocked}
        >
          <EditorInline
            Component={FormToggle}
            field='footerEnabled'
            value={template.footerEnabled}
            otherFieldOpen={(editing !== null && editing !== 'footerEnabled')}
            saveFormField={saveFormField}
            locked={featuresLocked}
          />
        </EditorFormRow>
        {
          template.footerEnabled || headerLocked
            ? (
              <>
                <EditorFormRow
                  label={<Translate content='pdf_editor_footer_text' />}
                  help={<HelpText content='pdf_editor_option_help_footer_text' />}
                  locked={featuresLocked}
                >
                  <Editor
                    Component={RichEditor}
                    maxLength={72}
                    allowNewLines={false}
                    displayBlockStyleControls={false}
                    field='footer'
                    preview={
                      <RichEditorPreview
                        content={template.footer}
                        defaultContent={
                          <Translate
                            content={featuresLocked ? 'pdf_default_branded_footer' : 'pdf_editor_option_default_content_empty_text'}
                          />
                        }
                      />
                    }
                    value={template.footer}
                    editingValue={editingValue}
                    editing={editing === 'footer'}
                    otherFieldOpen={(editing !== null && editing !== 'footer')}
                    setEditingValue={setEditingValue}
                    setEditingField={setEditingField}
                    resetEditing={resetEditing}
                    saveFormField={saveFormField}
                    locked={featuresLocked}
                  />
                </EditorFormRow>
                <EditorFormRow
                  label={<Translate content='pdf_editor_footer_color' />}
                  help={<HelpText content='pdf_editor_option_help_footer_color' />}
                  locked={featuresLocked}
                >
                  <Editor
                    field='colorScheme.footerColor'
                    preview={<ColorPreview colors={(template.colorScheme || {}).footerColor} />}
                    Component={ColorPickers}
                    value={template.colorScheme.footerColor}
                    editingValue={editingValue}
                    editing={editing === 'colorScheme.footerColor'}
                    otherFieldOpen={(editing !== null && editing !== 'colorScheme.footerColor')}
                    setEditingValue={setEditingValue}
                    setEditingField={setEditingField}
                    resetEditing={resetEditing}
                    locked={featuresLocked}
                    saveFormField={({ value }) => saveFormField({
                      field: 'colorScheme',
                      value: {
                        ...template.colorScheme,
                        footerColor: value
                      }
                    })}
                  />
                </EditorFormRow>
                <EditorFormRow
                  label={<Translate content='pdf_editor_footer_logo' />}
                  help={<HelpText content='pdf_editor_option_help_footer_logo' />}
                  locked={featuresLocked}
                >
                  <Editor
                    Component={ImageUploader}
                    field='footerLogo'
                    fileUpload
                    preview={<ImagePreview field='footerLogo' image={template.footerLogo} />}
                    value={template.footerLogo}
                    editingValue={editingValue}
                    editing={editing === 'footerLogo'}
                    otherFieldOpen={(editing !== null && editing !== 'footerLogo')}
                    setEditingValue={setEditingValue}
                    setEditingField={setEditingField}
                    resetEditing={resetEditing}
                    savePdfFile={savePdfFile}
                    locked={featuresLocked}
                  />
                </EditorFormRow>
              </>
              )
            : (
              <EditorFormRow infoLabel>
                <InfoLabel style='default'>
                  <Icon type='information' style={{ marginRight: 8 }} />
                  <Translate content='pdf_editor_footer_info' />
                </InfoLabel>
              </EditorFormRow>
              )
        }
      </Container>
    </>
  );
};

TabFooter.propTypes = {
  template: PropTypes.shape({
    name: PropTypes.string
  })
};

export default React.memo(TabFooter);
